import React, { useState } from 'react';
import './HelpPage.css';

function FeatureCard({ icon, title, badge, children }) {
  return (
    <div className="hp-wf-card">
      <div className="hp-wf-card-icon">{icon}</div>
      <h3 className="hp-wf-card-title">{title}</h3>
      {badge && <span className="hp-wf-badge">{badge}</span>}
      <p className="hp-wf-card-desc">{children}</p>
    </div>
  );
}

function Section({ icon, title, desc, children }) {
  return (
    <section className="hp-section">
      <div className="hp-section-header">
        {icon && <span className="hp-section-icon">{icon}</span>}
        <h2 className="hp-section-title">{title}</h2>
      </div>
      {desc && <p className="hp-section-desc">{desc}</p>}
      {children}
    </section>
  );
}

function FlowStep({ n, title, children }) {
  return (
    <div className="hp-step">
      <div className="hp-step-num">{n}</div>
      <div className="hp-step-body">
        <h4 className="hp-step-title">{title}</h4>
        <p className="hp-step-desc">{children}</p>
      </div>
    </div>
  );
}

function Tip({ children }) {
  return (
    <div className="hp-tip">
      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" className="hp-tip-icon">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
      </svg>
      <span>{children}</span>
    </div>
  );
}

function Pill({ children }) {
  return <span className="hp-pill">{children}</span>;
}

function EditorFeature({ icon, title, children }) {
  return (
    <div className="hp-editor-feature">
      <div className="hp-editor-feature-icon">{icon}</div>
      <div>
        <strong>{title}</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

function AccessCard({ icon, title, desc, highlight }) {
  return (
    <div className={`hp-access-card${highlight ? ' hp-access-card--highlight' : ''}`}>
      <span className="hp-access-card-icon">{icon}</span>
      <div>
        <strong className="hp-access-card-title">{title}</strong>
        <p className="hp-access-card-desc">{desc}</p>
      </div>
    </div>
  );
}

const LANGUAGES = [
  { region: 'European', langs: ['French', 'German', 'Spanish', 'Portuguese', 'Italian', 'Dutch', 'Polish', 'Romanian', 'Ukrainian'] },
  { region: 'African', langs: ['Swahili', 'Yoruba', 'Igbo', 'Hausa', 'Amharic', 'Zulu'] },
  { region: 'Asian & Middle East', langs: ['Chinese', 'Japanese', 'Korean', 'Hindi', 'Arabic'] },
];

const FAQS = [
  {
    id: 'q1',
    q: 'How long does transcription take?',
    a: 'Most sermons are ready within 5–15 minutes depending on length. The system processes audio much faster than real time, so even a three-hour service does not take long. You can leave the page and return later — the transcript will be waiting for you.',
  },
  {
    id: 'q2',
    q: 'What audio and video formats are accepted for transcription?',
    a: 'MP3, MP4, WAV, M4A, FLAC, MOV, MKV, AVI, and WebM are all supported. If you upload a video file, the system extracts the audio automatically — only the audio is processed.',
  },
  {
    id: 'q3',
    q: 'What file types can be translated?',
    a: 'PDF files, Word documents (DOCX), plain text files (TXT), and subtitle files (SRT). The translated output preserves the original structure — paragraphs and headings stay in place. Note: PDF files are output as Word documents since images cannot be recovered from PDF.',
  },
  {
    id: 'q4',
    q: 'Can I edit the transcript after it is ready?',
    a: 'Yes. The transcript opens in a review editor where you can click any paragraph to hear the audio at that point, then double-click to correct the text. Changes are saved back immediately. You can also switch between language tabs if translations have been requested.',
  },
  {
    id: 'q5',
    q: 'How do I fix a name or term that keeps coming out wrong?',
    a: 'Add it to the Glossary. Go to the Glossary page, click Add Term, enter what the system wrote in "Original" and what it should say in "Corrected". From that point on, the correction is applied automatically to every new sermon — you will never have to fix it manually again.',
  },
  {
    id: 'q6',
    q: 'Can I trim the recording before uploading?',
    a: 'Yes. On the Transcription upload page, enable "Trim audio before upload". A visual waveform editor opens where you can drag handles to set the start and end points, listen to a preview, and confirm the trim. Only the trimmed portion is uploaded.',
  },
  {
    id: 'q7',
    q: 'What is Interpreter Mode?',
    a: 'If your recording includes a live interpreter already speaking alongside the preacher — for example, the preacher speaks in English and an interpreter immediately repeats in French — Interpreter Mode separates the two language streams into independent transcripts rather than mixing them together.',
  },
  {
    id: 'q8',
    q: 'What if something goes wrong or a recording fails?',
    a: 'If a recording shows as failed, use the Start Over button to try uploading it again — most failures are temporary. If the problem repeats, contact your administrator and let them know which recording it was and roughly when you uploaded it.',
  },
];

export default function HelpPage({ isPendingAccess = false, onBack }) {
  const [openFaq, setOpenFaq] = useState(null);
  const toggle = (id) => setOpenFaq(prev => prev === id ? null : id);

  return (
    <div className="hp-root">

      {/* ── Hero ── */}
      <div className="hp-hero">
        <div className="hp-hero-inner">
          {onBack && (
            <button className="hp-back-link" onClick={onBack}>← Back</button>
          )}
          <div className="hp-church-wordmark">Godstone Tabernacle</div>
          <h1 className="hp-hero-title">{onBack ? 'Sermon Studio' : 'Help & Guide'}</h1>
          <p className="hp-hero-sub">
            {onBack
              ? 'Transcribe sermons. Translate documents.'
              : 'Everything you need to know about transcribing and translating sermons.'}
          </p>
          {isPendingAccess && (
            <div className="hp-pending">
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
              <span>
                Your account has been created and is awaiting approval.
                An administrator will grant you access shortly.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Feature cards ── */}
      <div className="hp-pillars-wrap">
        <div className="hp-pillars">
          <FeatureCard icon="🎙️" title="Transcription" badge="Audio · Video">
            Upload any sermon recording and receive a complete text transcript
            in minutes, with each speaker marked. Click any line to hear the
            audio at that exact moment. Edit inline, save, and export as a
            Word document, plain text, or subtitle file.
          </FeatureCard>
          <FeatureCard icon="🌍" title="Translation" badge="PDF · Word · TXT · SRT">
            Upload sermon notes, an outline, or a subtitle file and receive
            translated versions in 20+ languages. Each translated document
            preserves the original structure — headings, paragraphs, and
            formatting intact.
          </FeatureCard>
          <FeatureCard icon="📚" title="Glossary" badge="Auto-applied">
            Build a library of corrections for pastor names, ministry terms,
            programme names, and scripture references. Once saved, every
            correction is applied automatically to future sermons — you never
            have to fix the same mistake twice.
          </FeatureCard>
        </div>
      </div>

      <div className="hp-content">

        {/* ── Transcription workflow ── */}
        <Section
          icon="🎙️"
          title="How to transcribe a sermon"
          desc="From recording to finished transcript — here is what happens at each step."
        >
          <FlowStep n="1" title="Upload your recording">
            Go to the <Pill>Transcription</Pill> page and drag your audio or video
            file onto the upload area, or click to browse. MP3, MP4, WAV, M4A,
            FLAC, MOV and other common formats are all accepted. There is no file
            size or length limit.
          </FlowStep>
          <FlowStep n="2" title="Trim if needed (optional)">
            If the recording includes a long period of silence, music, or
            pre-service content you do not need transcribed, enable
            <Pill>Trim audio before upload</Pill>. A waveform editor opens — drag
            the handles to your start and end points, preview the selection, and
            confirm.
          </FlowStep>
          <FlowStep n="3" title="Choose your mode">
            Select <Pill>Standard</Pill> for a regular sermon.
            If the recording already has a live interpreter speaking alongside the
            preacher, select <Pill>Interpreter Mode</Pill> — the system will
            separate the two language streams into independent transcripts.
          </FlowStep>
          <FlowStep n="4" title="The system processes the recording">
            The AI listens to the full recording, identifies each speaker, and
            produces a transcript. You will see the status update live. Most
            sermons are ready within 5–15 minutes — you can leave the page and
            return when it is done.
          </FlowStep>
          <FlowStep n="5" title="Review, correct, and export">
            The transcript opens in the review editor. Read through it,
            click any paragraph to play the audio at that point, and double-click
            to correct any text. Save when satisfied, then export as TXT, Word,
            or SRT subtitles — with or without timestamps.
          </FlowStep>
          <Tip>
            The first recording of the day takes a few extra minutes while the system
            starts up. Later recordings the same day process immediately.
          </Tip>
        </Section>

        {/* ── Translation workflow ── */}
        <Section
          icon="🌍"
          title="How to translate a document"
          desc="Translate sermon notes and outlines so congregations in other languages can receive them too."
        >
          <FlowStep n="1" title="Prepare your document">
            The Translation page accepts PDF files, Word documents (.docx),
            plain text files (.txt), and subtitle files (.srt). Any standard
            sermon outline, script, or notes file will work. PDFs must be
            text-based — scanned images cannot be translated.
          </FlowStep>
          <FlowStep n="2" title="Upload and select languages">
            Go to the <Pill>Translation</Pill> page and drop your file onto the
            upload area. Then tick the languages you need from the list — you can
            select multiple languages at once and they will all be translated
            together.
          </FlowStep>
          <FlowStep n="3" title="The system translates each paragraph">
            The document is translated paragraph by paragraph into each chosen
            language. Any glossary terms you have saved for those languages are
            applied automatically, keeping church-specific names and phrases
            consistent throughout.
          </FlowStep>
          <FlowStep n="4" title="Review the output">
            When ready, the translated output opens with a tab for each language.
            Switch between tabs to check each translation. Double-click any
            paragraph to correct a term — especially names, scripture references,
            or ministry-specific phrases.
          </FlowStep>
          <FlowStep n="5" title="Download the translated file">
            Click <Pill>Download</Pill> on any language tab to save the finished
            file. Word documents and text files are downloaded in their original
            format. SRT subtitle files download ready to load into your streaming
            software.
          </FlowStep>
          <Tip>
            Save corrections to the glossary after reviewing — the next
            translation will be even more accurate without any extra effort.
          </Tip>
        </Section>

        {/* ── Editor guide ── */}
        <Section
          icon="✏️"
          title="Using the review editor"
          desc="The editor opens automatically after transcription or translation. Here is what each feature does."
        >
          <div className="hp-editor-guide">
            <EditorFeature icon="🔊" title="Click a timestamp to play">
              Every paragraph shows a timestamp on the left. Click it to jump to
              that moment in the audio and play from there — useful for checking
              unclear words in context without scrolling through the whole file.
            </EditorFeature>
            <EditorFeature icon="✏️" title="Double-click to edit">
              Double-click any paragraph to open it for editing. A text area
              appears with the paragraph content. Make your corrections, then
              click outside or press Escape to close.
            </EditorFeature>
            <EditorFeature icon="💾" title="Save your changes">
              Click <Pill>Save</Pill> in the toolbar after making corrections.
              Your edits are stored permanently — if you close and reopen the
              transcript, all corrections will still be there.
            </EditorFeature>
            <EditorFeature icon="🌐" title="Switch between languages">
              If translations were requested, language tabs appear at the top of
              the editor. Click any tab to switch between the English transcript
              and each translated version. Each language is independently
              editable and saveable.
            </EditorFeature>
            <EditorFeature icon="📤" title="Export">
              Use the export options to download the transcript as a plain text
              file, a Word-compatible document, or an SRT subtitle file — each
              available with or without timestamps.
            </EditorFeature>
          </div>
        </Section>

        {/* ── Glossary guide ── */}
        <Section
          icon="📚"
          title="Improving accuracy with the glossary"
          desc="The glossary is how the system learns the church's specific vocabulary — pastor names, programme names, scripture shorthand, and ministry terms."
        >
          <div className="hp-glossary-how">
            <div className="hp-glossary-step">
              <div className="hp-glossary-arrow">
                <strong>Heard by AI</strong>
                <span className="hp-code">pastor jon adeyemy</span>
                <em>before correction</em>
              </div>
              <div className="hp-glossary-middle">→</div>
              <div className="hp-glossary-arrow">
                <strong>You correct it</strong>
                <span className="hp-code">Pastor John Adeyemi</span>
                <em>saved to glossary</em>
              </div>
              <div className="hp-glossary-middle">→</div>
              <div className="hp-glossary-arrow">
                <strong>Next sermon</strong>
                <span className="hp-code">Pastor John Adeyemi ✓</span>
                <em>applied automatically</em>
              </div>
            </div>
          </div>

          <FlowStep n="1" title="Open the Glossary page">
            Click <Pill>Glossary</Pill> in the navigation. If you have
            transcription access, the English glossary is shown — corrections
            here improve the accuracy of future transcriptions. If you have
            translation access, select your language from the dropdown to manage
            glossary terms for that language.
          </FlowStep>
          <FlowStep n="2" title="Add a term">
            Click <Pill>Add Term</Pill>. In the <em>Original</em> field, enter
            exactly what the system wrote incorrectly. In the <em>Corrected</em>{' '}
            field, enter what it should say. Choose a category (Names, Scripture,
            Names, Places, Terminology, Scripture, or Other) to keep the glossary organised.
          </FlowStep>
          <FlowStep n="3" title="The correction applies automatically">
            On every future transcription or translation, your saved terms are
            applied as part of the processing. The more the glossary grows, the
            less manual correction is needed after each sermon.
          </FlowStep>
          <Tip>
            Build the glossary gradually as you find errors during review.
            A focused list of 20–30 terms covering your ministry's specific
            vocabulary makes a significant difference to accuracy.
          </Tip>
        </Section>

        {/* ── Languages ── */}
        <Section
          icon="🌐"
          title="Available translation languages"
          desc="GT Sermon Studio supports translation into 20 languages across Europe, Africa, and Asia."
        >
          <div className="hp-lang-grid">
            {LANGUAGES.map(({ region, langs }) => (
              <div key={region} className="hp-lang-group">
                <h4 className="hp-lang-region">{region}</h4>
                <div className="hp-lang-tags">
                  {langs.map(l => (
                    <span key={l} className="hp-lang-tag">{l}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Tip>
            Each user's translation access is limited to the specific languages
            assigned by an administrator. Contact your administrator if you need
            a language added to your account.
          </Tip>
        </Section>

        {/* ── Access levels ── */}
        <Section
          icon="🔑"
          title="Access levels"
          desc="An administrator at Godstone Tabernacle assigns your access level when they approve your account. Each level gives access to specific tools."
        >
          <div className="hp-access-cards">
            <AccessCard
              icon="🎙️"
              title="Transcription"
              desc="Upload and transcribe audio recordings. Review and edit transcripts. Export as TXT, Word, or SRT. Access to the English terminology glossary."
            />
            <AccessCard
              icon="🌍"
              title="Translation"
              desc="Translate sermon documents (PDF, Word, TXT, SRT) into your assigned languages. Review, edit, and download translated files. Access to your assigned language glossaries."
            />
            <AccessCard
              icon="✨"
              title="Full Access"
              desc="Both transcription and translation together. All features from both levels, including the English glossary and all assigned language glossaries."
              highlight
            />
            <AccessCard
              icon="⚙️"
              title="Admin"
              desc="Everything in Full Access, plus user management (assign access levels and languages to team members) and usage reports."
            />
          </div>
          {isPendingAccess && (
            <div className="hp-access-cta">
              Your account is awaiting approval. Once an administrator reviews it,
              you will gain access to the tools assigned to you.
            </div>
          )}
        </Section>

        {/* ── FAQ ── */}
        <Section icon="❓" title="Common questions">
          {FAQS.map(({ id, q, a }) => (
            <div key={id} className={`hp-faq-item${openFaq === id ? ' open' : ''}`}>
              <button className="hp-faq-q" onClick={() => toggle(id)}>
                <span>{q}</span>
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" className="hp-faq-chevron">
                  <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                </svg>
              </button>
              {openFaq === id && <p className="hp-faq-a">{a}</p>}
            </div>
          ))}
        </Section>

      </div>
    </div>
  );
}
